import { BrowserRouter, Routes, Route, useSearchParams, useLocation } from 'react-router-dom';
import { PropertyProvider } from '@/context/PropertyContext';
import { StayProvider } from '@/context/StayContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LuxuryStyles } from '@/components/LuxuryStyles';
import {
  DevHome,
  Embed,
  Landing,
  ExperiencePage,
  BookPage,
  SuccessPage,
  SimpleDemoLanding,
} from '@/pages';

/**
 * Check if this is the showcase page (root without property param)
 * Shows demo showcase on both localhost and production
 */
function useIsShowcasePage(): boolean {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Showcase shows at root path without property param
  return location.pathname === '/' && !searchParams.has('property');
}

/**
 * Check if dev mode is explicitly requested via ?dev=true
 * This allows access to the DevHome for testing
 */
function useIsDevMode(): boolean {
  const [searchParams] = useSearchParams();
  return searchParams.get('dev') === 'true';
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

/**
 * Main app router
 * - DemoShowcase on root without property param (all environments)
 * - DevHome accessible via ?dev=true for testing
 * - Normal booking flow for everything else
 */
function AppRoutes() {
  const isShowcasePage = useIsShowcasePage();
  const isDevMode = useIsDevMode();

  // DevHome for testing (only accessible via ?dev=true)
  if (isDevMode) {
    return <DevHome />;
  }

  // Showcase page doesn't need PropertyProvider - render it directly
  if (isShowcasePage) {
    return <SimpleDemoLanding />;
  }

  // All other routes need PropertyProvider and StayProvider for property config and stay dates
  return (
    <PropertyProvider>
      <StayProvider>
        <PropertyRoutes />
      </StayProvider>
    </PropertyProvider>
  );
}

/**
 * Routes that require PropertyProvider context
 */
function PropertyRoutes() {
  return (
    <>
      <LuxuryStyles />
      <Routes>
        {/* Embed mode routes - property key in URL */}
        <Route path="/embed/:propertyKey" element={<Embed />} />
        <Route path="/embed/:propertyKey/:experienceSlug" element={<ExperiencePage />} />
        <Route path="/embed/:propertyKey/:experienceSlug/book" element={<BookPage />} />

        {/* Root with property param shows Landing */}
        <Route path="/" element={<Landing />} />

        {/* Landing mode routes - property from subdomain/domain */}
        <Route path="/:experienceSlug" element={<ExperiencePage />} />
        <Route path="/:experienceSlug/book" element={<BookPage />} />

        {/* Shared routes */}
        <Route path="/success" element={<SuccessPage />} />

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="mt-2 text-muted-foreground">Page not found</p>
      </div>
    </div>
  );
}
